import { useState, useEffect } from "react";
import useProcessos from "../../hooks/useProcessos";
import useAdvogados from "../../hooks/useAdvogados";
import useClientes from "../../hooks/useClientes";
import useFinanceiro from "../../hooks/useFinanceiro";
import useUsuarios from "../../hooks/useUsuarios";

// 👈 Importando o hook exportado pelo seu AuthContext
import { useAuth } from "../../auth/AuthContext"; 

import ModalValorCausa from "../../components/processo/ValorCausa";
import { formatarNomeUpper } from "../../utils/formatadores";

const CadastrarProcesso = () => {
  const [selected, setSelected] = useState(false);
  const { cadastrarProcesso } = useProcessos();
  const { advogados, loading: loadingAdvogados } = useAdvogados();
  const { clientes } = useClientes();
  const { criarValorCausa } = useFinanceiro();
  const { usuarios, loading: loadingUsuarios } = useUsuarios();
  const [actionType, setActionType] = useState(null);
  
  // 👈 Pega o usuário logado do seu AuthContext
  const { user } = useAuth(); 

  const [form, setForm] = useState({
    numeroProcesso: "",
    dataAjuizamento: "",
    autor: "",
    clienteId: "",
    advogadoId: "",
    areaDireito: "",
    faseAcao: "",
    temParceiro: false,
    parceiroId: "",
    quemAjuizouId: "",
    valorCausa: ""
  });

  // 🚀 Atualiza o ID de quem ajuizou automaticamente quando o usuário logado estiver carregado
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        quemAjuizouId: user._id || user.id || ""
      }));
    }
  }, [user]);

  const handleChange = (campo, valor) => {
    setForm((prev) => ({
      ...prev,
      [campo]: valor
    }));
  };

  const salvarProcesso = async (e) => {
    e.preventDefault();

    if (!form.clienteId) {
      alert("Selecione um cliente");
      return;
    }

    if (!form.advogadoId) {
      alert("Selecione um advogado responsável");
      return;
    }

    // 💡 Mapeia e limpa campos nulos antes de enviar
    const payload = {
      ...form,
      parceiroId: form.temParceiro && form.parceiroId ? form.parceiroId : null,
      quemAjuizouId: form.quemAjuizouId || user?._id || user?.id || null,
    };

    setActionType("salvar");

    try {
      // 🟢 USANDO O PAYLOAD AQUI
      const processo = await cadastrarProcesso(payload);
      console.log("Processo criado:", processo);

      if (form.valorCausa) {
        await criarValorCausa({
          clienteId: form.clienteId,
          processoId: processo._id,
          valorTotal: form.valorCausa
        });
      }
      alert("Processo cadastrado com sucesso!");
    } catch (err) {
      console.error(err);
      alert("Erro ao cadastrar processo");
    } finally {
      setActionType(null);
    }

    // Reset do formulário mantendo o ID do usuário logado
    setForm({
      numeroProcesso: "",
      dataAjuizamento: "",
      autor: "",
      clienteId: "",
      advogadoId: "",
      areaDireito: "",
      faseAcao: "",
      temParceiro: false,
      parceiroId: "",
      quemAjuizouId: user?._id || user?.id || "",
      valorCausa: ""
    });
  };

  return (
    <div className="container">
      <div className="card">
        <h1>CADASTRAR PROCESSO</h1>

        <form onSubmit={salvarProcesso}>
  <div className="form-grid">
    {/* CLIENTE */}
    <div className="form-group">
      <label>
        Cliente <span style={{ color: "red" }}>*</span>
      </label>
      <select
        required
        className="input"
        value={form.clienteId}
        onChange={(e) => {
          const cliente = clientes.find(
            (c) => c._id === e.target.value
          );

          setForm({
            ...form,
            clienteId: e.target.value,
            autor: cliente?.nomeCliente || ""
          });
        }}
      >
        <option value="">Selecione o cliente</option>
        {clientes.map((c) => (
          <option key={c._id} value={c._id}>
            {formatarNomeUpper(c.nomeCliente)}
          </option>
        ))}
      </select>
    </div>

    {/* AUTOR (BLOQUEADO) */}
    <div className="form-group">
      <label>Autor</label>
      <input
        className="input"
        type="text"
        value={formatarNomeUpper(form.autor)}
        readOnly
      />
    </div>

    {/* NÚMERO DO PROCESSO */}
    <div className="form-group">
      <label>
        Número do Processo <span style={{ color: "red" }}>*</span>
      </label>
      <input
        required
        className="input"
        type="text"
        value={form.numeroProcesso}
        onChange={(e) =>
          handleChange("numeroProcesso", e.target.value)
        }
      />
    </div>

    {/* DATA */}
    <div className="form-group">
      <label>
        Data do Ajuizamento <span style={{ color: "red" }}>*</span>
      </label>
      <input
        required
        className="input"
        type="date"
        value={form.dataAjuizamento}
        onChange={(e) =>
          handleChange("dataAjuizamento", e.target.value)
        }
      />
    </div>

    {/* ADVOGADO */}
    <div className="form-group">
      <label>
        Advogado Responsável <span style={{ color: "red" }}>*</span>
      </label>
      <select
        required
        className="input"
        disabled={loadingAdvogados}
        value={form.advogadoId}
        onChange={(e) =>
          handleChange("advogadoId", e.target.value)
        }
      >
        <option value="">
          {loadingAdvogados
            ? "Carregando advogados..."
            : "Selecione o advogado"}
        </option>
        {advogados.map((adv) => (
          <option key={adv._id} value={adv._id}>
            {formatarNomeUpper(adv.nome)}
          </option>
        ))}
      </select>
    </div>

    {/* ÁREA */}
    <div className="form-group">
      <label>
        Área do Direito <span style={{ color: "red" }}>*</span>
      </label>
      <select
        required
        className="input"
        value={form.areaDireito}
        onChange={(e) =>
          handleChange("areaDireito", e.target.value)
        }
      >
        <option value="">Selecione</option>
        <option value="Cível">Cível</option>
        <option value="Trabalhista">Trabalhista</option>
        <option value="Previdenciário">Previdenciário</option>
        <option value="Criminal">Criminal</option>
      </select>
    </div>

    {/* FASE */}
    <div className="form-group">
      <label>
        Fase da Ação <span style={{ color: "red" }}>*</span>
      </label>
      <select
        required
        className="input"
        value={form.faseAcao}
        onChange={(e) =>
          handleChange("faseAcao", e.target.value)
        }
      >
        <option value="">Selecione</option>
        <option value="Administrativo">Administrativo</option>
        <option value="Protocolo">Protocolo</option>
        <option value="Inicial">Inicial</option>
        <option value="Audiência">Audiência</option>
        <option value="Sentença">Sentença</option>
        <option value="Execucao">Execução</option>
      </select>
    </div>

    {/* PARCEIRO */}
    <div className="form-group">
      <label>
        <input
          type="checkbox"
          checked={form.temParceiro}
          onChange={(e) => {
            const checked = e.target.checked;
            setForm((prev) => ({
              ...prev,
              temParceiro: checked,
              parceiroId: checked ? prev.parceiroId : ""
            }));
          }}
        />
        Possui parceiro
      </label>
    </div>

    {form.temParceiro && (
      <div className="form-group">
        <label>Parceiro</label>
        <select
          className="input"
          disabled={loadingUsuarios}
          value={form.parceiroId}
          onChange={(e) =>
            handleChange("parceiroId", e.target.value)
          }
        >
          <option value="">
            {loadingUsuarios
              ? "Carregando parceiros..."
              : "Selecione o parceiro"}
          </option>
          {usuarios.map((u) => (
            <option key={u._id || u.id} value={u._id || u.id}>
              {formatarNomeUpper(u.nome || u.name || u.email)}
            </option>
          ))}
        </select>
      </div>
    )}

    {/* QUEM AJUIZOU (SOMENTE LEITURA) */}
    <div className="form-group">
      <label>Quem Ajuizou</label>
      <input
        className="input"
        type="text"
        value={formatarNomeUpper(user?.nome || user?.name || user?.email || "")}
        readOnly
      />
    </div>
  </div>

  <button 
    className="botao-primary" 
    type="submit"
    disabled={actionType !== null}
  >
    {actionType === "salvar" ? (
      <span className="spinner-loader"></span>
    ) : (
      "Cadastrar Processo"
    )}
  </button>
</form>
      </div>

      {selected && (
        <ModalValorCausa
          onClose={() => setSelected(false)}
          onSalvar={(valor) => {
            setForm((prev) => ({
              ...prev,
              valorCausa: valor
            }));
            setSelected(false);
          }}
        />
      )}
    </div>
  );
};

export default CadastrarProcesso;